import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, Check, Wand2 } from 'lucide-react';
import { api } from '../../api/client';
import type { RateSheetItemUsage, QboItemSummary } from '../../types/qbo-mapping';

/**
 * RATE-SHEET ROWS → QUICKBOOKS ITEMS (API 0406).
 *
 * Why the row and not the line type: TNT bills casing tong and integral tong as
 * SEPARATE QuickBooks items, and FieldIQ has one `power_tongs` type for both.
 * The rate sheet already tells them apart, so the binding lives on the row, and
 * a power_tongs line whose row is unmapped REFUSES to push rather than falling
 * back to a type-level guess.
 *
 * Three things make this usable at TNT's scale (measured 2026-09-19: 355 active
 * rows across 7 sheets, ~316 distinct names, a few dozen ever billed):
 *
 *   1. MOST-USED FIRST. The API sorts by lines linked to the row, then by lines
 *      whose description matches its name. The rows that will block a push are
 *      at the top; the long tail of never-billed bid rows is below them.
 *   2. BULK. Tick several rows, pick one item, apply — the casing-tong row on
 *      every sheet is one decision, not seven.
 *   3. SAME NAME, OTHER SHEET. Every new bid sheet arrives completely unmapped.
 *      When a same-named row elsewhere is bound (and all such rows agree), the
 *      row offers that item. It is a SUGGESTION: nothing is applied until the
 *      operator clicks, because a name match is not proof of the same service.
 */

const PAGE = 50;

function unwrap<T>(body: { data?: T } | T | undefined): T | undefined {
  if (body && typeof body === 'object' && 'data' in body) return (body as { data?: T }).data;
  return body as T | undefined;
}

export default function QboRateSheetItemMapping({ notify }: { notify: (text: string, tone?: 'success' | 'error') => void }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkItem, setBulkItem] = useState('');
  const [search, setSearch] = useState('');
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const [limit, setLimit] = useState(PAGE);

  const rowsQ = useQuery({
    queryKey: ['qbo', 'needs', 'rate_sheet_item'],
    queryFn: async () => unwrap((await api.quickbooks.mappings.rateSheetItems()).data) ?? [],
  });
  // Same key as the Line items tab — both pick from the realm's ITEMS.
  const itemsQ = useQuery({
    queryKey: ['qbo', 'candidates', 'item'],
    queryFn: async () => {
      const rows = unwrap((await api.quickbooks.mappings.realmItems()).data) ?? [];
      return rows.map((r: QboItemSummary) => ({
        id: r.id, label: r.fullyQualifiedName || r.name, active: r.active, hint: r.incomeAccountName ?? undefined,
      }));
    },
  });

  const rowsData = rowsQ.data;
  const itemsData = itemsQ.data;
  const rows: RateSheetItemUsage[] = useMemo(() => rowsData ?? [], [rowsData]);
  const items = useMemo(() => itemsData ?? [], [itemsData]);
  const itemLabel = (id: string) => items.find((i) => i.id === id)?.label ?? `#${id}`;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) =>
      (!unmappedOnly || !r.binding) &&
      (!q || r.serviceName.toLowerCase().includes(q) || (r.sheetName ?? '').toLowerCase().includes(q)));
  }, [rows, search, unmappedOnly]);

  const mappedCount = rows.filter((r) => r.binding).length;
  const blocking = rows.filter((r) => !r.binding && r.linkedLineCount > 0).length;
  const withSuggestion = rows.filter((r) => !r.binding && r.suggestion);

  const save = useMutation({
    mutationFn: (v: { ids: string[]; qboId: string }) =>
      api.quickbooks.mappings.saveRateSheetItems({ rate_sheet_item_ids: v.ids, qbo_id: v.qboId }),
    onSuccess: (_res, v) => {
      queryClient.invalidateQueries({ queryKey: ['qbo'] });
      setSelected(new Set());
      setBulkItem('');
      notify(`${v.ids.length} row${v.ids.length === 1 ? '' : 's'} mapped to ${itemLabel(v.qboId)}`);
    },
    onError: (err: any) => notify(err?.response?.data?.error || 'Failed to save mapping', 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.quickbooks.mappings.remove('rate_sheet_item', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qbo'] });
      notify('Mapping removed');
    },
    onError: (err: any) => notify(err?.response?.data?.error || 'Failed to remove mapping', 'error'),
  });

  /** Replacing a binding is a change, and the operator is told what it replaces. */
  const confirmReplace = (ids: string[], qboId: string): boolean => {
    const replacing = rows.filter((r) => ids.includes(r.rateSheetItemId) && r.binding && r.binding.qboId !== qboId);
    if (replacing.length === 0) return true;
    return window.confirm(
      `${replacing.length} of these row${replacing.length === 1 ? ' is' : 's are'} already mapped to a different item:\n\n` +
      replacing.slice(0, 8).map((r) => `• ${r.serviceName} → ${r.binding!.qboName ?? `#${r.binding!.qboId}`}`).join('\n') +
      (replacing.length > 8 ? `\n…and ${replacing.length - 8} more` : '') +
      `\n\nReplace with ${itemLabel(qboId)}? Future invoices will post these lines to the new item.`
    );
  };

  const apply = (ids: string[], qboId: string) => {
    if (!qboId || ids.length === 0) return;
    if (!confirmReplace(ids, qboId)) return;
    save.mutate({ ids, qboId });
  };

  /** Apply every unambiguous suggestion, one bulk call per target item. */
  const applyAllSuggestions = async () => {
    const byItem = new Map<string, string[]>();
    for (const r of withSuggestion) {
      const list = byItem.get(r.suggestion!.qboId) ?? [];
      list.push(r.rateSheetItemId);
      byItem.set(r.suggestion!.qboId, list);
    }
    if (!window.confirm(
      `Map ${withSuggestion.length} row${withSuggestion.length === 1 ? '' : 's'} to the item a same-named row on another sheet uses?\n\n` +
      'Check the list first if any of your sheets reuse a name for a different service.'
    )) return;
    try {
      for (const [qboId, ids] of Array.from(byItem.entries())) {
        await api.quickbooks.mappings.saveRateSheetItems({ rate_sheet_item_ids: ids, qbo_id: qboId });
      }
      notify(`${withSuggestion.length} suggestion${withSuggestion.length === 1 ? '' : 's'} applied`);
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Failed to apply suggestions', 'error');
    } finally {
      queryClient.invalidateQueries({ queryKey: ['qbo'] });
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  if (rowsQ.isLoading || itemsQ.isLoading) {
    return (
      <div className="flex items-center gap-2 text-fieldiq-muted text-sm">
        <Loader2 size={16} className="animate-spin" /> Loading from QuickBooks…
      </div>
    );
  }
  if (rowsQ.isError || itemsQ.isError) {
    const code = ((rowsQ.error ?? itemsQ.error) as any)?.response?.data?.code;
    if (code === 'QBO_NOT_CONNECTED') {
      return <p className="text-sm text-white">QuickBooks is not connected. Connect it under Integrations first.</p>;
    }
    return (
      <div className="flex items-center gap-2 text-red-400 text-sm">
        <AlertCircle size={16} /> Could not load rate sheet rows or QuickBooks items.
      </div>
    );
  }

  const shown = visible.slice(0, limit);
  const allShownSelected = shown.length > 0 && shown.every((r) => selected.has(r.rateSheetItemId));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-xs text-fieldiq-muted">
        <span data-testid="rsi-mapped-count">{mappedCount} of {rows.length} rows mapped</span>
        {blocking > 0 && (
          <span data-testid="rsi-blocking-count" className="flex items-center gap-1 text-amber-400">
            <AlertCircle size={12} />
            {blocking} used on tickets and not mapped — {blocking === 1 ? 'it blocks' : 'they block'} a push
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setLimit(PAGE); }}
          placeholder="Search rows or sheets"
          aria-label="Search rate sheet rows"
          className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-white focus:border-fieldiq-accent focus:outline-none w-64"
        />
        <label className="flex items-center gap-2 text-sm text-fieldiq-muted">
          <input type="checkbox" checked={unmappedOnly} onChange={(e) => { setUnmappedOnly(e.target.checked); setLimit(PAGE); }} />
          Unmapped only
        </label>
        {withSuggestion.length > 0 && (
          <button
            onClick={applyAllSuggestions}
            disabled={save.isPending}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-fieldiq-card border border-fieldiq-border text-white hover:border-fieldiq-accent"
          >
            <Wand2 size={14} /> Apply {withSuggestion.length} same-name suggestion{withSuggestion.length === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {selected.size > 0 && (
        <div data-testid="rsi-bulk-bar" className="flex flex-wrap items-center gap-3 bg-fieldiq-card border border-fieldiq-accent rounded-lg px-3 py-2">
          <span className="text-sm text-white">{selected.size} selected</span>
          <select
            aria-label="QuickBooks item for the selected rows"
            value={bulkItem}
            onChange={(e) => setBulkItem(e.target.value)}
            className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-white text-sm min-w-[16rem]"
          >
            <option value="">— pick a QuickBooks item —</option>
            {items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}{c.active ? '' : '  (archived in QuickBooks)'}{c.hint ? ` — ${c.hint}` : ''}
              </option>
            ))}
          </select>
          <button
            onClick={() => apply(Array.from(selected), bulkItem)}
            disabled={!bulkItem || save.isPending}
            className="px-3 py-1.5 rounded-lg text-sm bg-fieldiq-accent text-fieldiq-dark font-semibold disabled:opacity-50"
          >
            Map {selected.size} row{selected.size === 1 ? '' : 's'}
          </button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-fieldiq-muted hover:text-white">
            Clear
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-fieldiq-muted border-b border-fieldiq-border">
              <th className="py-2 pr-2 w-8">
                <input
                  type="checkbox"
                  aria-label="Select all shown rows"
                  checked={allShownSelected}
                  onChange={() => setSelected((prev) => {
                    const next = new Set(prev);
                    for (const r of shown) {
                      if (allShownSelected) next.delete(r.rateSheetItemId); else next.add(r.rateSheetItemId);
                    }
                    return next;
                  })}
                />
              </th>
              <th className="py-2 pr-4 font-medium">Rate sheet row</th>
              <th className="py-2 pr-4 font-medium">Used</th>
              <th className="py-2 pr-4 font-medium">QuickBooks item</th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-fieldiq-muted">No rate sheet rows match.</td></tr>
            )}
            {shown.map((r) => (
              <tr key={r.rateSheetItemId} className="border-b border-fieldiq-border/50 align-top">
                <td className="py-2 pr-2">
                  <input
                    type="checkbox"
                    aria-label={`Select ${r.serviceName} on ${r.sheetName ?? 'unknown sheet'}`}
                    checked={selected.has(r.rateSheetItemId)}
                    onChange={() => toggle(r.rateSheetItemId)}
                  />
                </td>
                <td className="py-2 pr-4">
                  <div className="text-white">{r.serviceName}</div>
                  <div className="text-xs text-fieldiq-muted">{r.sheetName ?? '—'}</div>
                </td>
                <td className="py-2 pr-4 text-fieldiq-muted whitespace-nowrap">
                  <div>{r.linkedLineCount} line{r.linkedLineCount === 1 ? '' : 's'}</div>
                  {r.nameMatchLineCount > 0 && (
                    <div className="text-xs" title="Ticket lines on any sheet whose description matches this row's name. A ranking hint only.">
                      {r.nameMatchLineCount} by name
                    </div>
                  )}
                </td>
                <td className="py-2 pr-4">
                  <select
                    aria-label={`QuickBooks item for ${r.serviceName} on ${r.sheetName ?? 'unknown sheet'}`}
                    value={r.binding?.qboId ?? ''}
                    disabled={save.isPending || remove.isPending}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) { remove.mutate(r.rateSheetItemId); return; }
                      if (!confirmReplace([r.rateSheetItemId], v)) {
                        e.target.value = r.binding?.qboId ?? '';
                        return;
                      }
                      save.mutate({ ids: [r.rateSheetItemId], qboId: v });
                    }}
                    className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-white focus:border-fieldiq-accent focus:outline-none min-w-[14rem]"
                  >
                    <option value="">— not mapped —</option>
                    {/* A binding to an id the catalog no longer holds is shown, not swallowed. */}
                    {r.binding && !items.some((c) => c.id === r.binding!.qboId) && (
                      <option value={r.binding.qboId} disabled>
                        {`⚠ #${r.binding.qboId}${r.binding.qboName ? ` (${r.binding.qboName})` : ''} — not found in this QuickBooks company`}
                      </option>
                    )}
                    {items.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}{c.active ? '' : '  (archived in QuickBooks)'}{c.hint ? ` — ${c.hint}` : ''}
                      </option>
                    ))}
                  </select>
                  {!r.binding && r.suggestion && (
                    <button
                      onClick={() => apply([r.rateSheetItemId], r.suggestion!.qboId)}
                      disabled={save.isPending}
                      className="block mt-1 text-xs text-fieldiq-accent hover:underline text-left"
                    >
                      Same as on {r.suggestion.fromSheetName ?? 'another sheet'}: {r.suggestion.qboName ?? `#${r.suggestion.qboId}`}
                    </button>
                  )}
                  {!r.binding && r.suggestionAmbiguous && (
                    <div className="mt-1 text-xs text-fieldiq-muted">
                      Rows with this name on other sheets use different items — pick one.
                    </div>
                  )}
                </td>
                <td className="py-2 whitespace-nowrap">
                  {r.binding
                    ? <span className="flex items-center gap-1 text-green-400 text-xs"><Check size={13} /> mapped</span>
                    : r.linkedLineCount > 0
                      ? <span className="flex items-center gap-1 text-amber-400 text-xs"><AlertCircle size={13} /> blocks a push</span>
                      : <span className="text-xs text-fieldiq-muted">not mapped</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visible.length > shown.length && (
        <button
          onClick={() => setLimit(visible.length)}
          className="px-3 py-1.5 rounded-lg text-sm bg-fieldiq-card border border-fieldiq-border text-white hover:border-fieldiq-accent"
        >
          Show all {visible.length} rows
        </button>
      )}
    </div>
  );
}
